import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  BarChart3, Users, Building2, TrendingUp, FileText, 
  Clock, Activity, Loader2, RefreshCw, Calendar,
  ArrowUpRight, ArrowDownRight, UserPlus, CheckCircle,
  XCircle, AlertTriangle
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PlatformAnalyticsDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, all

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    if (!loading) setRefreshing(true);
    try {
      const res = await api.get(`/admin/settings/platform-analytics?range=${timeRange}`);
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (!isMainAdmin) {
    return (
      <Sidebar>
        <div className="p-8 text-center">
          <p className="text-red-600">Access denied. Main Admin only.</p>
        </div>
      </Sidebar>
    );
  }

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Sidebar>
    );
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, trendLabel }) => (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 min-w-0 overflow-hidden">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1 truncate">{title}</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900">{value?.toLocaleString() || 0}</p>
          {subtitle && <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">{subtitle}</p>}
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-xs sm:text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend >= 0 ? <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" /> : <ArrowDownRight className="w-3.5 h-3.5 flex-shrink-0" />}
              <span className="truncate">{Math.abs(trend)}% {trendLabel || 'vs last period'}</span>
            </div>
          )}
        </div>
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-8 bg-gray-50/50 min-h-screen overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Platform Analytics</h1>
            <p className="text-gray-500 mt-1">Monitor platform-wide usage and growth metrics</p>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Time Range Selector */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 sm:px-4 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>

            <button
              onClick={fetchAnalytics}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <StatCard
            title="Total Companies"
            value={stats?.companies?.total}
            subtitle={`${stats?.companies?.active || 0} active`}
            icon={Building2}
            color="bg-blue-600"
            trend={stats?.companies?.growth}
          />
          <StatCard
            title="Total Users"
            value={stats?.users?.total}
            subtitle={`${stats?.users?.verified || 0} verified`}
            icon={Users}
            color="bg-green-600"
            trend={stats?.users?.growth}
          />
          <StatCard
            title="Quote Requests"
            value={stats?.quotes?.total}
            subtitle={`${stats?.quotes?.completed || 0} completed`}
            icon={FileText}
            color="bg-purple-600"
            trend={stats?.quotes?.growth}
          />
          <StatCard
            title="Active Sessions"
            value={stats?.sessions?.active}
            subtitle="Currently online"
            icon={Activity}
            color="bg-orange-600"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          {/* Company Status Breakdown */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 lg:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Company Status
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-600">Approved</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{stats?.companies?.approved || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-gray-600">Pending</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{stats?.companies?.pending || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-gray-600">Rejected</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{stats?.companies?.rejected || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Suspended</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{stats?.companies?.suspended || 0}</span>
              </div>
            </div>
          </div>

          {/* User Tier Distribution */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 lg:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              User Tiers
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Gold</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 sm:w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-500 rounded-full"
                      style={{ width: `${(stats?.tiers?.gold / stats?.users?.total * 100) || 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">{stats?.tiers?.gold || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Silver</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 sm:w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gray-400 rounded-full"
                      style={{ width: `${(stats?.tiers?.silver / stats?.users?.total * 100) || 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">{stats?.tiers?.silver || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Bronze</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 sm:w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-600 rounded-full"
                      style={{ width: `${(stats?.tiers?.bronze / stats?.users?.total * 100) || 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">{stats?.tiers?.bronze || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">None</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 sm:w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gray-300 rounded-full"
                      style={{ width: `${(stats?.tiers?.none / stats?.users?.total * 100) || 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-12 text-right">{stats?.tiers?.none || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Referral Stats */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 lg:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Referral Program
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Invites Sent</span>
                <span className="text-lg font-bold text-gray-900">{stats?.referrals?.total_invites || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Signups via Referral</span>
                <span className="text-lg font-bold text-gray-900">{stats?.referrals?.converted || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Conversion Rate</span>
                <span className="text-lg font-bold text-purple-600">{stats?.referrals?.conversion_rate || 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Active Referrers</span>
                <span className="text-lg font-bold text-gray-900">{stats?.referrals?.active_referrers || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity & Top Companies */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          {/* Recent Registrations */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 lg:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Registrations</h3>
            {stats?.recent_users?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_users.map((user, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {user.name || user.email}
                      </p>
                      <p className="text-xs text-gray-500">{user.company_name || 'No company'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{user.created_at}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        user.email_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {user.email_verified ? 'Verified' : 'Pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent registrations</p>
            )}
          </div>

          {/* Top Companies by Users */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 lg:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Companies by Users</h3>
            {stats?.top_companies?.length > 0 ? (
              <div className="space-y-3">
                {stats.top_companies.map((company, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-600">{idx + 1}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{company.name}</p>
                        <p className="text-xs text-gray-500">{company.subdomain}.trados.app</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">{company.user_count}</p>
                      <p className="text-xs text-gray-500">users</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No company data available</p>
            )}
          </div>
        </div>

        {/* Last Updated */}
        <p className="mt-6 text-sm text-gray-500 text-right">
          Last updated: {new Date().toLocaleString()}
        </p>
      </div>
    </Sidebar>
  );
};

export default PlatformAnalyticsDashboard;
